import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'

import DateTimeDisplay from '../../components/DateTimeDisplay'
import DetailPageTemplate from '../../templates/DetailPageTemplate'
import fetchWithAuth from '../../utilities/fetchWithAuth'

const AnalysisChainPage = () => {
  const { uuid } = useParams()
  const [analysisChain, setAnalysisChain] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    const fetchAnalysisChain = async () => {
      setIsLoading(true)
      try {
        const { data, status } = await fetchWithAuth(`analysis-chains/${uuid}`)
        if (status === 200) {
          setAnalysisChain(data)
        } else {
          throw new Error(`HTTP error! status: ${status}`)
        }
      } catch (err) {
        setError(err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchAnalysisChain()
  }, [uuid])

  const handleCloseDialog = () => {
    setOpenDeleteDialog(false)
  }

  const handleDelete = async () => {
    try {
      await fetchWithAuth(`analysis-chains/${uuid}`, { method: 'DELETE' })
      navigate('/analysis-chains')
    } catch (error) {
      console.error('Error deleting analysis chain:', error)
    }
  }

  if (error) {
    return <Typography color="error">Error: {error.message}</Typography>
  }

  if (isLoading || !analysisChain) {
    return <DetailPageTemplate isLoading={true} />
  }

  const sections = [
    {
      title: 'General Information',
      content: (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={3}>
              <Box display="flex" alignItems="flex-start" gap={2}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ width: 500, flexShrink: 0 }}
                >
                  Name
                </Typography>
                <Typography>{analysisChain.name}</Typography>
              </Box>
              <Box display="flex" alignItems="flex-start" gap={2}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ width: 500, flexShrink: 0 }}
                >
                  Description
                </Typography>
                <Typography>{analysisChain.description}</Typography>
              </Box>
              <Box display="flex" alignItems="flex-start" gap={2}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ width: 500, flexShrink: 0 }}
                >
                  Config
                </Typography>
                <Stack direction="row" spacing={1}>
                  {
                    <Chip
                      label={
                        analysisChain.is_auto_compatible
                          ? 'Auto Compatible'
                          : 'Not auto compatible'
                      }
                      color={
                        analysisChain.is_auto_compatible ? 'info' : 'disabled'
                      }
                      size="small"
                    />
                  }
                  {
                    <Chip
                      label={analysisChain.is_basic ? 'Basic' : 'Not Basic'}
                      color={analysisChain.is_basic ? 'info' : 'disabled'}
                      size="small"
                    />
                  }
                </Stack>
              </Box>
              <Box display="flex" alignItems="flex-start" gap={2}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ width: 500, flexShrink: 0 }}
                >
                  Last Analysis
                </Typography>
                <Typography>{analysisChain.lastAnalysisName}</Typography>
              </Box>
              <Box display="flex" alignItems="flex-start" gap={2}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ width: 500, flexShrink: 0 }}
                >
                  Created At
                </Typography>
                <DateTimeDisplay date={new Date(analysisChain.createdAt)} />
              </Box>
            </Stack>
          </CardContent>
        </Card>
      ),
    },
    {
      title: 'Analyses',
      content: (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              {analysisChain.analyses.map((analysis, index) => (
                <Box key={analysis.uuid}>
                  <Typography variant="body1" component="span" sx={{ mr: 1 }}>
                    {index + 1}.
                  </Typography>
                  <Chip
                    label={analysis.name}
                    component={Link}
                    to={`/analyses/${analysis.uuid}`}
                    clickable
                    variant="outlined"
                  />
                  {analysis.inputDataTypes &&
                    analysis.inputDataTypes.length > 0 && (
                      <Box sx={{ mt: 1, ml: 4 }}>
                        <Typography variant="caption" color="text.secondary">
                          Input Data Types:
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 0.5, flexWrap: 'wrap' }}
                        >
                          {analysis.inputDataTypes.map((dataType) => (
                            <Chip
                              key={dataType._id}
                              label={dataType.name}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}
                  {analysis.outputDataTypes &&
                    analysis.outputDataTypes.length > 0 && (
                      <Box sx={{ mt: 1, ml: 4 }}>
                        <Typography variant="caption" color="text.secondary">
                          Output Data Types:
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ mt: 0.5, flexWrap: 'wrap' }}
                        >
                          {analysis.outputDataTypes.map((dataType) => (
                            <Chip
                              key={dataType.uuid}
                              label={dataType.name}
                              size="small"
                              variant="outlined"
                              color="secondary"
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}
                  {(() => {
                    let allParameters = {}

                    if (analysisChain?.allParameters) {
                      allParameters = {
                        ...analysisChain.analysis_parameters[analysis._id],
                      }
                    }

                    if (Object.keys(allParameters).length > 0) {
                      return (
                        <Box sx={{ mt: 1, ml: 4 }}>
                          <Typography variant="caption" color="text.secondary">
                            Parameters:
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{ mt: 0.5, flexWrap: 'wrap' }}
                          >
                            {Object.entries(allParameters).map(
                              ([key, value]) => (
                                <Chip
                                  key={key}
                                  label={`${key}: ${value}`}
                                  size="small"
                                  sx={{ m: 0.5 }}
                                />
                              ),
                            )}
                          </Stack>
                        </Box>
                      )
                    }
                    return null
                  })()}
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>
      ),
    },
    {
      title: 'Chain Input Data Types',
      content: (
        <Card variant="outlined">
          <CardContent>
            <Stack
              direction="row"
              spacing={1}
              sx={{ flexWrap: 'wrap', gap: 1 }}
            >
              {analysisChain.inputDataTypes.map((dataType) => (
                <Chip
                  key={dataType.uuid}
                  label={dataType.name}
                  component={Link}
                  to={`/data-types/${dataType.uuid}`}
                  clickable
                  variant="outlined"
                />
              ))}
            </Stack>
          </CardContent>
        </Card>
      ),
    },
  ]

  return (
    <>
      <DetailPageTemplate
        title={analysisChain.name ?? 'Analysis Chain'}
        breadcrumbs={
          analysisChain
            ? ['analysis-chains', analysisChain.name ?? 'analysis-chain']
            : undefined
        }
        actions={
          <Stack direction="row" spacing={2}>
            <Button
              component={Link}
              to={`/analysis-chains/${uuid}/edit`}
              variant="contained"
              color="primary"
            >
              Edit Analysis Chain
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setOpenDeleteDialog(true)}
            >
              Delete Analysis Chain
            </Button>
          </Stack>
        }
        sections={sections}
      />

      <Dialog open={openDeleteDialog} onClose={handleCloseDialog}>
        <DialogTitle>Delete Analysis Chain</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this Analysis Chain? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default AnalysisChainPage
